import { FormApi } from "final-form";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";

import { ControlledJsonTextarea } from "@dashboard/shared/components/JsonTextarea";
import { TopicHeader } from "@dashboard/shared/components/TopicHeader";
import { useDeviceRegistrationGroup } from "@dashboard/shared/hooks/useDeviceRegistrationGroup";
import { Box, Switch } from "@mui/material";
import { SecondaryButton } from "@shared/CustomButton";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SelectOption } from "@shared/form/SelectField/SelectField.types";
import { UncontrolledTextField } from "@shared/form/TextField";
import { LoadingIcon } from "@shared/Loading";
import { Modal } from "@shared/Modal/Modal";

import * as Utils from "./GenerateQRCodeDialog.utils";
import { useStyles } from "./GenerateQRCodeDialogStyles";
import { QrCode } from "./QrCode";

const enrollmentLink = "https://emteria.com/kb/device-enrollment-using-qr-code";

type TextInputHandler = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    form: FormApi<Utils.FormValues, Partial<Utils.FormValues>>,
) => void;

interface Props {
    isOpen: boolean;
    groupId: number;
    initialValues: Utils.FormValues;
    onClose: React.Dispatch<React.SetStateAction<boolean>>;
}

const GenerateQRCodeDialog = (props: Props) => {
    const [advancedMode, setAdvancedMode] = React.useState(false);
    const [jsonError, setJsonError] = React.useState<string | null>(null);
    const [version, setVersion] = React.useState("v60100000");
    const [wifiConnection, setWifiConnection] = React.useState(0);

    const { t } = useTranslation(["modals", "common"]);
    const { classes } = useStyles();
    const validate = useValidationSchema(Utils.schema);
    const { groupOptions, ensureUniversalLicense, isCreatingLicense, allUniversalLicenses } = useDeviceRegistrationGroup();

    const formKey = React.useMemo(() => `${props.groupId}-${props.isOpen}`, [props.groupId, props.isOpen]);
    const [registrationGroupId, setRegistrationGroupId] = React.useState<number>(props.groupId);

    const onEditorChange = (value: string, form: FormApi<Utils.FormValues>) => {
        try {
            const obj: Utils.FormValues = JSON.parse(value);
            form.restart(obj);

            const ulValue = obj["android.app.extra.PROVISIONING_ADMIN_EXTRAS_BUNDLE"]?.["UL"];
            if (ulValue) {
                const license = allUniversalLicenses.find((l) => l.activationCode === ulValue);
                if (license?.universalGroupId) {
                    setRegistrationGroupId(license.universalGroupId);
                } else {
                    setRegistrationGroupId(props.groupId);
                }
            } else {
                setRegistrationGroupId(props.groupId);
            }

            setJsonError(null);
        } catch (_error) {
            setJsonError(t("modals:generateQrCode.toast.invalidJsonFormat"));
        }
    };
    const onAdvanceModeChange = () => {
        setAdvancedMode(!advancedMode);
        setJsonError(null);
    };
    const onWifiConnectionChange = async (selectedOption: SelectOption | null, form: FormApi<Utils.FormValues>) => {
        if (!selectedOption) {
            return;
        }
        if (Number.isNaN(Number(selectedOption.value))) {
            return;
        }
        const values = form.getState().values;
        const value = Number(selectedOption.value);

        setWifiConnection(value);
        if (value === 0) {
            form.reset({
                ...values,
                "android.app.extra.PROVISIONING_WIFI_SSID": undefined,
                "android.app.extra.PROVISIONING_WIFI_PASSWORD": undefined,
                "android.app.extra.PROVISIONING_WIFI_SECURITY_TYPE": undefined,
            });
        }
    };
    const onSystemApplicationChange = async (selectedOption: SelectOption | null, form: FormApi<Utils.FormValues>) => {
        if (!selectedOption) {
            return;
        }
        if (Number.isNaN(Number(selectedOption.value))) {
            return;
        }
        const values = form.getState().values;
        const value = Number(selectedOption.value);

        form.reset({
            ...values,
            "android.app.extra.PROVISIONING_LEAVE_ALL_SYSTEM_APPS_ENABLED": !!value,
        });
    };
    const onEncryptionChange = async (selectedOption: SelectOption | null, form: FormApi<Utils.FormValues>) => {
        if (!selectedOption) {
            return;
        }
        if (Number.isNaN(Number(selectedOption.value))) {
            return;
        }
        const values = form.getState().values;
        const value = Number(selectedOption.value);

        form.reset({ ...values, "android.app.extra.PROVISIONING_SKIP_ENCRYPTION": !!value });
    };
    const onRegistrationGroupChange = async (selectedOption: SelectOption | null, form: FormApi<Utils.FormValues>) => {
        if (!selectedOption) {
            return;
        }
        if (Number.isNaN(Number(selectedOption.value))) {
            return;
        }

        const values = form.getState().values;
        const selectedGroupId = Number(selectedOption.value);
        setRegistrationGroupId(selectedGroupId);

        const licenseCode = await ensureUniversalLicense(selectedGroupId);

        if (licenseCode) {
            form.reset({
                ...values,
                "android.app.extra.PROVISIONING_ADMIN_EXTRAS_BUNDLE": { "UL": licenseCode },
            });
        } else {
            form.reset({
                ...values,
                "android.app.extra.PROVISIONING_ADMIN_EXTRAS_BUNDLE": undefined,
            });
        }
    };
    const onVersionChange = (selectedOption: SelectOption | null, form: FormApi<Utils.FormValues>) => {
        const values = form.getState().values;
        const value = selectedOption?.value;
        const option = Utils.versionMDM.find((item) => item.value === value);

        if (option && typeof value === "string") {
            setVersion(value);
            form.reset({
                ...values,
                "android.app.extra.PROVISIONING_DEVICE_ADMIN_PACKAGE_CHECKSUM": option.checksum,
                "android.app.extra.PROVISIONING_DEVICE_ADMIN_PACKAGE_DOWNLOAD_LOCATION": option.url,
            });
        }
    };
    const onWifiSSIDChange: TextInputHandler = (e, form) => {
        const values = form.getState().values;
        if (typeof e.target.value === "string") {
            form.reset({ ...values, "android.app.extra.PROVISIONING_WIFI_SSID": e.target.value });
        }
    };
    const onWifiPAsswordChange: TextInputHandler = (e, form) => {
        const values = form.getState().values;
        if (typeof e.target.value === "string") {
            form.reset({ ...values, "android.app.extra.PROVISIONING_WIFI_PASSWORD": e.target.value });
        }
    };
    const onSecurityTypeChange = async (selectedOption: SelectOption | null, form: FormApi<Utils.FormValues>) => {
        if (!selectedOption) {
            return;
        }
        if (typeof selectedOption.value !== "string") {
            return;
        }
        const values = form.getState().values;
        const value = selectedOption.value;
        form.reset({ ...values, "android.app.extra.PROVISIONING_WIFI_SECURITY_TYPE": value });
    };
    const onProvisioningModeChange = async (selectedOption: SelectOption | null, form: FormApi<Utils.FormValues>) => {
        if (!selectedOption) {
            return;
        }

        const numberValue = Number(selectedOption.value);
        if (numberValue !== 1 && numberValue !== 2) {
            return;
        }

        const values = form.getState().values;
        form.reset({ ...values, "android.app.extra.PROVISIONING_MODE": numberValue });
    };

    return (
        <Form<Utils.FormValues>
            key={formKey}
            initialValues={props.initialValues}
            validate={validate}
            onSubmit={() => undefined}
            render={({ values, form }) => {
                return (
                    <form>
                        <Modal
                            title={t("modals:generateQrCode.title")}
                            open={props.isOpen}
                            onClose={() => props.onClose(false)}
                            classes={{ paper: classes.dialog }}
                            startButton={
                                <SecondaryButton onClick={() => window.open(enrollmentLink, "_blank")}>
                                    {t("common:button.getHelp")}
                                </SecondaryButton>
                            }
                        >
                            <div className={classes.dialogTitleContainer}>
                                <div className={classes.modeContainer}>
                                    <TopicHeader>{t("modals:generateQrCode.advancedMode")}</TopicHeader>
                                    <Switch checked={advancedMode} onChange={onAdvanceModeChange} color="primary" />
                                </div>
                            </div>
                            <Box className={classes.dialogContent}>
                                {advancedMode ? (
                                    <Box className={classes.dataContainer} style={{ alignItems: "flex-end" }}>
                                        <ControlledJsonTextarea<Utils.FormValues>
                                            defaultValue={values}
                                            error={jsonError}
                                            onChange={onEditorChange}
                                            note={t("modals:generateQrCode.note")}
                                        />
                                        {isCreatingLicense ? (
                                            <div className={classes.loaderContainerAdvanced}>
                                                <LoadingIcon size="block" />
                                            </div>
                                        ) : (
                                            <QrCode value={JSON.stringify(values, undefined, 2)} advancedMode />
                                        )}
                                    </Box>
                                ) : null}

                                {!advancedMode ? (
                                    <div className={classes.dataContainer}>
                                        <div className={classes.contentBox}>
                                            <SelectField
                                                value={Utils.versionMDM.find((item) => item.value === version)}
                                                label={t("modals:generateQrCode.input.emteriaMdm.caption")}
                                                options={Utils.versionMDM}
                                                onChange={(_event, selectedOption) => onVersionChange(selectedOption, form)}
                                                className={classes.input}
                                                fullWidth
                                            />
                                            <SelectField
                                                label={t("modals:generateQrCode.input.deviceRegistration.caption")}
                                                value={groupOptions.find((item) => item.value === registrationGroupId)}
                                                options={groupOptions}
                                                onChange={(_event, selectedOption) =>
                                                    onRegistrationGroupChange(selectedOption, form)
                                                }
                                                className={classes.input}
                                                fullWidth
                                                disableSorting
                                            />

                                            <SelectField
                                                label={t("modals:generateQrCode.input.encryption.caption")}
                                                value={Utils.encryptionOptions.find(
                                                    (item) =>
                                                        item.value ===
                                                        (values?.["android.app.extra.PROVISIONING_SKIP_ENCRYPTION"] ? 1 : 0),
                                                )}
                                                options={Utils.encryptionOptions}
                                                onChange={(_event, selectedOption) => onEncryptionChange(selectedOption, form)}
                                                className={classes.input}
                                                fullWidth
                                            />
                                            <SelectField
                                                label={t("modals:generateQrCode.input.systemApplications.caption")}
                                                value={Utils.systemAppsOptions.find(
                                                    (item) =>
                                                        item.value ===
                                                        (values?.["android.app.extra.PROVISIONING_LEAVE_ALL_SYSTEM_APPS_ENABLED"]
                                                            ? 1
                                                            : 0),
                                                )}
                                                options={Utils.systemAppsOptions}
                                                onChange={(_event, selectedOption) =>
                                                    onSystemApplicationChange(selectedOption, form)
                                                }
                                                className={classes.input}
                                                fullWidth
                                            />
                                            <SelectField
                                                label={t("modals:generateQrCode.input.provisioningMode.caption")}
                                                value={Utils.provisioningModeOptions.find(
                                                    (item) =>
                                                        item.value === Number(values?.["android.app.extra.PROVISIONING_MODE"]),
                                                )}
                                                options={Utils.provisioningModeOptions}
                                                onChange={(_event, selectedOption) =>
                                                    onProvisioningModeChange(selectedOption, form)
                                                }
                                                className={classes.input}
                                                fullWidth
                                            />
                                        </div>
                                        <div className={classes.contentBox}>
                                            <SelectField
                                                label={t("modals:generateQrCode.input.wifiConnection.caption")}
                                                value={Utils.wifiOptions.find((item) => item.value === wifiConnection)}
                                                options={Utils.wifiOptions}
                                                onChange={(_event, selectedOption) =>
                                                    onWifiConnectionChange(selectedOption, form)
                                                }
                                                className={classes.input}
                                                fullWidth
                                            />

                                            {wifiConnection ? (
                                                <>
                                                    <UncontrolledTextField
                                                        value={values?.["android.app.extra.PROVISIONING_WIFI_SSID"]}
                                                        label={t("modals:generateQrCode.input.wifiSsid.caption")}
                                                        onChange={(e) => onWifiSSIDChange(e, form)}
                                                        className={classes.input}
                                                        fullWidth
                                                    />
                                                    <UncontrolledTextField
                                                        value={values?.["android.app.extra.PROVISIONING_WIFI_PASSWORD"]}
                                                        label={t("modals:generateQrCode.input.wifiPassword.caption")}
                                                        onChange={(e) => onWifiPAsswordChange(e, form)}
                                                        className={classes.input}
                                                        fullWidth
                                                    />
                                                    <SelectField
                                                        value={Utils.securityTypeOptions.find(
                                                            (item) =>
                                                                item.value ===
                                                                values?.["android.app.extra.PROVISIONING_WIFI_SECURITY_TYPE"],
                                                        )}
                                                        label={t("modals:generateQrCode.input.securityType.caption")}
                                                        onChange={(_event, selectedOption) =>
                                                            onSecurityTypeChange(selectedOption, form)
                                                        }
                                                        options={Utils.securityTypeOptions}
                                                        className={classes.input}
                                                    />
                                                </>
                                            ) : null}
                                        </div>
                                        {isCreatingLicense ? (
                                            <div className={classes.loaderContainer}>
                                                <LoadingIcon size="block" />
                                            </div>
                                        ) : (
                                            <QrCode value={JSON.stringify(values, undefined, 2)} advancedMode={false} />
                                        )}
                                    </div>
                                ) : null}
                            </Box>
                        </Modal>
                    </form>
                );
            }}
        />
    );
};

export default GenerateQRCodeDialog;
